// External recorder skip-trace agent — client for the /api/agent/*
// same-origin proxy routes (which add the X-Timberline-Key secret and
// forward to the agent box). The agent's response shapes are external and
// loosely specified, so they're typed as open records and read
// defensively at the call site (run_id / runId, queued / count, etc.).

import { authHeaders } from '../../lib/authHeaders';

export interface SkipTraceOwner {
  name: string;
  trs: string[];
  doc_numbers: string[];
  county: string | null;
}

export interface SkipTraceSubmitBody {
  label: string;
  owners: SkipTraceOwner[];
  /** Load the run's results into Close when it finishes. Defaults CHECKED
   *  in the UI. Exact agent submit-contract key: `load_close`. */
  load_close: boolean;
}

/** Whatever the agent returns — open shape, read defensively. */
export type AgentResponse = Record<string, unknown>;

async function jsonReq<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const agentApi = {
  /** Submit a skip-trace run. Returns the agent's response (typically
   *  { run_id, queued }). */
  submitSkipTrace: (body: SkipTraceSubmitBody) =>
    jsonReq<AgentResponse>('/api/agent/skiptrace', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /** Poll a run's status (typically { status, queued, running, done, failed }). */
  skipTraceStatus: (runId: string) =>
    jsonReq<AgentResponse>(`/api/agent/skiptrace/status/${encodeURIComponent(runId)}`),

  /** Submit a pasted Weld session (Cookie-Editor export). The agent
   *  live-validates it and reports { validated, login_state, weld_session }.
   *  sessionJson is the raw pasted string, sent as the request body. */
  refreshWeldSession: (sessionJson: string) =>
    jsonReq<AgentResponse>('/api/agent/session/weld', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: sessionJson,
    }),

  /** Live Weld session status (no body). Powers the global session chip;
   *  response carries `weld_session` (read with readWeldSession). */
  getWeldSessionStatus: () =>
    jsonReq<AgentResponse>('/api/agent/session/weld'),

  /** Active/recent title-agent runs (proxied). Read with readTitleRuns. */
  getTitleRuns: () =>
    jsonReq<AgentResponse>('/api/agent/title-runs'),
};

// ── Title-agent runs ─────────────────────────────────────────────────────
// The title agent's run-status shape isn't contractual, so read defensively:
// the response is a list of runs (under runs/active/jobs, or the top-level
// array), each carrying an id, a tract (trs), a stage + doc counts (flat or
// nested under progress/counts), a start time, and a status/error.
export interface TitleRunView {
  runId: string;
  tract: string | null;
  dealId: string | null;
  stage: string | null;
  detail: string | null;
  done: number | null;
  total: number | null;
  unit: string | null;
  startedAt: string | null;
  updatedAt: string | null;
  finishedAt: string | null;
  status: string | null;
  error: string | null;
  live: boolean | null;
  failed: boolean;
  terminal: boolean;
  active: boolean;
}

function str(v: unknown): string | null { return typeof v === 'string' && v.trim() ? v : null; }
function num(v: unknown): number | null { return typeof v === 'number' && Number.isFinite(v) ? v : null; }
function firstStr(o: any, ...keys: string[]): string | null {
  for (const k of keys) { const v = str(o?.[k]); if (v !== null) return v; }
  return null;
}
function firstNum(o: any, ...keys: string[]): number | null {
  for (const k of keys) { const v = num(o?.[k]); if (v !== null) return v; }
  return null;
}

// Terminal statuses. 'orphaned' (the agent's word for an OOM-killed / dead
// run) and its kin are terminal AND failures — matched by the regex below.
const TITLE_TERMINAL = new Set([
  'done', 'complete', 'completed', 'finished',
  'failed', 'error', 'aborted', 'cancelled', 'orphaned', 'killed', 'dead', 'stalled', 'timeout',
]);
const TITLE_FAILED_RE = /fail|error|abort|cancel|orphan|kill|oom|dead|stall|timeout|dropped/;
const TITLE_SUCCESS = new Set(['done', 'complete', 'completed', 'finished']);

export function readTitleRuns(r: AgentResponse | null): TitleRunView[] {
  const a = r as any;
  const list: any[] =
    Array.isArray(a) ? a :
    Array.isArray(a?.runs) ? a.runs :
    Array.isArray(a?.active) ? a.active :
    Array.isArray(a?.jobs) ? a.jobs :
    Array.isArray(a?.title_runs) ? a.title_runs : [];

  return list.map((run: any, i: number) => {
    const prog = (run?.progress && typeof run.progress === 'object') ? run.progress : run;
    const counts = (prog?.counts && typeof prog.counts === 'object') ? prog.counts : prog;
    const status = firstStr(run, 'status', 'state', 'phase');
    const message = firstStr(run, 'message', 'detail', 'note');
    const statusLc = (status ?? '').toLowerCase();
    const live = typeof run?.live === 'boolean' ? run.live : null;
    const finishedAt = firstStr(run, 'finished_at', 'finishedAt', 'ended_at', 'completed_at');

    const isSuccess = TITLE_SUCCESS.has(statusLc);
    const failed = !isSuccess && (TITLE_FAILED_RE.test(statusLc) || (!!finishedAt && !isSuccess && !!status && !TITLE_TERMINAL.has(statusLc)));
    const terminal = TITLE_TERMINAL.has(statusLc) || !!finishedAt || live === false;
    const active = live === true || (!terminal && !failed);

    // Doc/link count: structured counts if present, else parse "N links|docs"
    // out of the free-text message (the agent doesn't always fill counts).
    let done = firstNum(counts, 'done', 'completed', 'processed', 'fetched', 'links');
    let unit = firstStr(counts, 'unit');
    if (done === null && message) {
      const m = message.match(/\b(\d+)\s+(links|docs|documents|instruments)\b/i);
      if (m) { done = parseInt(m[1], 10); unit = m[2].toLowerCase(); }
    }

    return {
      runId: firstStr(run, 'run_id', 'id', 'runId', 'job_id') ?? `run_${i}`,
      tract: firstStr(run, 'trs', 'tract', 'trs_key', 'legal'),
      dealId: firstStr(run, 'deal_id', 'dealId', 'deal'),
      stage: firstStr(prog, 'stage', 'step', 'phase'),
      detail: firstStr(run, 'detail', 'note') ?? message,
      done,
      total: firstNum(counts, 'total', 'count', 'expected'),
      unit: unit ?? 'docs',
      startedAt: firstStr(run, 'started_at', 'startedAt', 'created_at', 'start'),
      updatedAt: firstStr(run, 'updated_at', 'updatedAt', 'last_update'),
      finishedAt,
      status,
      error: failed ? (message ?? status) : null,
      live,
      failed,
      terminal,
      active,
    };
  });
}

/** Prominent stage headline for a title run: "fetch — 9/16 docs". */
export function titleRunHeadline(run: TitleRunView): string {
  if (run.failed) return 'failed';
  const counts = (run.done != null && run.total != null)
    ? `${run.done}/${run.total}${run.unit ? ` ${run.unit}` : ''}`
    : (run.done != null ? `${run.done}${run.unit ? ` ${run.unit}` : ''}` : null);
  const stage = run.stage ?? (run.terminal ? 'done' : 'working');
  return counts ? `${stage} — ${counts}` : stage;
}

// ── Run progress (status endpoint's `progress` field) ───────────────────
// Exact contract: progress = { stage, detail, counts: { done, total, unit },
// started_at, updated_at }. Pinned to these keys (no variant probing).
export interface ProgressView {
  stage: string | null;      // "fetch"
  detail: string | null;     // free-text sub-detail
  done: number | null;       // 9
  total: number | null;      // 16
  unit: string | null;       // "docs"
  startedAt: string | null;  // ISO
  updatedAt: string | null;  // ISO
}

export function readProgress(r: AgentResponse | null): ProgressView | null {
  const p = (r as any)?.progress;
  if (!p || typeof p !== 'object') return null;
  const c = (p.counts && typeof p.counts === 'object') ? p.counts : {};
  return {
    stage:  typeof p.stage === 'string' ? p.stage : null,
    detail: typeof p.detail === 'string' ? p.detail : null,
    done:   typeof c.done === 'number' ? c.done : null,
    total:  typeof c.total === 'number' ? c.total : null,
    unit:   typeof c.unit === 'string' ? c.unit : null,
    startedAt: typeof p.started_at === 'string' ? p.started_at : null,
    updatedAt: typeof p.updated_at === 'string' ? p.updated_at : null,
  };
}

// Build the prominent stage headline, e.g. "fetch — 9/16 docs".
export function progressHeadline(p: ProgressView | null): string | null {
  if (!p || !p.stage) return null;
  const counts = (p.done != null && p.total != null)
    ? `${p.done}/${p.total}${p.unit ? ` ${p.unit}` : ''}`
    : null;
  return counts ? `${p.stage} — ${counts}` : p.stage;
}

// ── Final result (done) ─────────────────────────────────────────────────
export interface FinalView {
  traced: number | null;
  noMatch: number | null;
  estate: number | null;
  costUsd: number | null;
  smartViewUrl: string | null;
}
export function readFinal(r: AgentResponse | null): FinalView {
  const a = r as any;
  const url = typeof a?.smart_view_url === 'string' ? a.smart_view_url
            : typeof a?.close_smart_view_url === 'string' ? a.close_smart_view_url
            : typeof a?.smart_view === 'string' ? a.smart_view
            : (a?.smart_view && typeof a.smart_view?.url === 'string' ? a.smart_view.url : null);
  return {
    traced:  readCount(r, 'traced', 'traced_count', 'matched', 'done'),
    noMatch: readCount(r, 'no_match', 'no_match_count', 'unmatched', 'not_found'),
    estate:  readCount(r, 'estate', 'estate_flags', 'estates', 'estate_count', 'deceased'),
    costUsd: readCount(r, 'cost_usd', 'cost', 'total_cost', 'total_cost_usd'),
    smartViewUrl: url,
  };
}

// A run is done once the agent reports a terminal status, or once nothing
// is queued/running and at least one owner has finished. Defensive because
// the agent's status vocabulary isn't contractual.
const TERMINAL_STATUSES = new Set(['done', 'complete', 'completed', 'failed', 'error', 'finished', 'aborted', 'cancelled']);
export function isTerminalStatus(v: SkipTraceStatusView): boolean {
  if (v.status && TERMINAL_STATUSES.has(v.status.toLowerCase())) return true;
  const queued = v.queued ?? 0, running = v.running ?? 0, done = v.done ?? 0, failed = v.failed ?? 0;
  return queued === 0 && running === 0 && (done + failed) > 0;
}

// Terminal-but-failed (vs. done): the status string names a failure.
export function isFailedStatus(v: SkipTraceStatusView): boolean {
  return !!v.status && /fail|error|abort|cancel/.test(v.status.toLowerCase());
}

// The agent's 202 submit echoes the resolved load_close + which request
// key it read it from. Used to hard-fail loudly if the flag isn't honored.
export function readLoadCloseEcho(r: AgentResponse | null): { value: boolean | null; sourceKey: string | null } {
  const a = r as any;
  return {
    value: typeof a?.load_close === 'boolean' ? a.load_close : null,
    sourceKey: typeof a?.load_close_source_key === 'string' ? a.load_close_source_key : null,
  };
}

// The agent's session-validation verdict + login state.
export function readValidated(r: AgentResponse | null): boolean {
  const a = r as any;
  return a?.validated === true || a?.valid === true;
}

export function readLoginState(r: AgentResponse | null): string | null {
  const a = r as any;
  if (typeof a?.login_state === 'string') return a.login_state;
  if (typeof a?.loginState === 'string') return a.loginState;
  return null;
}

// ── Defensive field readers (the agent's exact keys aren't contractual) ──
export function readRunId(r: AgentResponse | null): string | null {
  if (!r) return null;
  const a = r as any;
  // Probe the top level and a couple of common wrappers, across the usual
  // id key spellings, so a 202 submit body's run id is found regardless of
  // exactly how the agent names/nests it.
  const objs = [a, a.run, a.data, a.result, a.job, a.batch].filter(o => o && typeof o === 'object');
  for (const o of objs) {
    const v = o.run_id ?? o.runId ?? o.runID ?? o.id
            ?? o.job_id ?? o.jobId ?? o.batch_id ?? o.batchId ?? o.task_id ?? o.taskId;
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

export function readCount(r: AgentResponse | null, ...keys: string[]): number | null {
  if (!r) return null;
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

/** Normalize the status response into the four counts the UI shows. */
export interface SkipTraceStatusView {
  status: string | null;          // overall run status if the agent reports one
  queued: number | null;
  running: number | null;
  done: number | null;
  failed: number | null;
}

export function readStatusView(r: AgentResponse | null): SkipTraceStatusView {
  const status = r && typeof r.status === 'string' ? r.status
               : r && typeof r.state === 'string' ? r.state
               : null;
  return {
    status,
    queued:  readCount(r, 'queued', 'queued_count', 'pending'),
    running: readCount(r, 'running', 'running_count', 'in_progress'),
    done:    readCount(r, 'done', 'done_count', 'completed', 'complete'),
    failed:  readCount(r, 'failed', 'failed_count', 'errors', 'error_count'),
  };
}

// ── Weld session state ───────────────────────────────────────────────────
// Exact contract. The agent nests session state under `weld_session` on the
// submit + status responses (and at GET /api/session/weld/status and
// /api/skiptrace/health):
//   { present, stale, needs_refresh: bool,
//     age: "1h05m" | null, age_seconds: int | null,
//     last_modified: ISO, warning: string when refresh needed }
// A submit blocked for a missing session also sets top-level
//   status="blocked_no_session", blocked=true, blocked_reason: string.
export interface WeldSession {
  present: boolean;
  stale: boolean;
  needsRefresh: boolean;
  age: string | null;          // e.g. "1h05m"
  ageSeconds: number | null;
  lastModified: string | null; // ISO
  warning: string | null;      // set when a refresh is needed
}

export function readWeldSession(r: AgentResponse | null): WeldSession | null {
  // Submit/status responses NEST the session under `weld_session`; the
  // GET /api/session/weld/status response returns the same fields FLAT at
  // the top level. Accept either — prefer the nested object, fall back to
  // the envelope itself when it carries the session shape directly.
  const a = r as any;
  const s = a?.weld_session && typeof a.weld_session === 'object'
    ? a.weld_session
    : (a && (typeof a.present === 'boolean' || typeof a.age === 'string' || typeof a.age_seconds === 'number') ? a : null);
  if (!s || typeof s !== 'object') return null;
  return {
    present:      s.present === true,
    stale:        s.stale === true,
    needsRefresh: s.needs_refresh === true,
    age:          typeof s.age === 'string' ? s.age : null,
    ageSeconds:   typeof s.age_seconds === 'number' ? s.age_seconds : null,
    lastModified: typeof s.last_modified === 'string' ? s.last_modified : null,
    warning:      typeof s.warning === 'string' ? s.warning : null,
  };
}

// Banner trigger: the session needs a refresh or is absent, or the submit
// was explicitly blocked for a missing session.
export function isSessionBlocked(r: AgentResponse | null): boolean {
  const a = r as any;
  if (a?.blocked === true) return true;
  if (typeof a?.status === 'string' && a.status === 'blocked_no_session') return true;
  const s = readWeldSession(r);
  return !!s && (s.needsRefresh || !s.present);
}

// The reason string the agent supplies when it blocks / warns, if any.
export function readBlockReason(r: AgentResponse | null): string | null {
  const a = r as any;
  if (typeof a?.blocked_reason === 'string' && a.blocked_reason.trim()) return a.blocked_reason;
  return readWeldSession(r)?.warning ?? null;
}

// Fallback age formatting when only age_seconds is present (age string null).
export function formatAge(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600), m = Math.round((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}
